import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { TextAreaField } from '@/components/ui/TextAreaField';

import { reasonError, requestError } from '../presentation';
import type { DocumentRequest } from '../types';
import { useRejectRequest } from '../use-document-requests';

// Rechazo de una solicitud (G3-1): acción destructiva con confirmación (UX-FB-01) y un
// motivo obligatorio que recibe el solicitante en su aviso.

export function RejectModal({
  request,
  onBack,
  onDone,
}: {
  request: DocumentRequest;
  onBack: () => void;
  onDone: (resolved: DocumentRequest) => void;
}): ReactElement {
  const reject = useRejectRequest(request.id);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirm(): Promise<void> {
    setChecked(true);
    if (busy || reasonError(reason) !== null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(await reject.mutateAsync(reason.trim()));
    } catch (err) {
      setError(requestError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmModal
      open
      title="Rechazar solicitud"
      destructive
      busy={busy}
      confirmLabel="Rechazar solicitud"
      cancelLabel="Volver"
      onCancel={onBack}
      onConfirm={() => void confirm()}
      message={
        <div className="form-grid">
          <p className="fld-note">
            «{request.proposedTitle}» quedará rechazada y{' '}
            {request.requester.displayName ?? 'quien la pidió'} recibirá el motivo.
          </p>
          <TextAreaField
            label="Motivo del rechazo"
            value={reason}
            maxLength={1000}
            placeholder="Por ejemplo: el procedimiento ya existe como PR-CAL-02"
            error={checked ? reasonError(reason) : null}
            onChange={setReason}
          />
          {error !== null && (
            <p className="fld-error" role="alert">
              {error}
            </p>
          )}
        </div>
      }
    />
  );
}
